import { fetchWithErrorHandling, buildGetOptions, buildPostOptions } from "../FetchUtils";
import { useAuthUser } from "react-auth-kit";
import { useContext } from "react";
import { AppContext } from "../../utils/AppContext";
import { getBaseURL } from "../../utils/Environment";

const endpoint = (baseUrl: string, action: string): string => {
  // just a safeguard to ensure we don't end up with double slashes in the URL
  const cleanAction = action.startsWith("/") ? action.slice(1) : action;
  return new URL(`/v1/registry/token-management/${cleanAction}`, baseUrl).toString();
};

export enum AUTH_TOKEN_TYPES {
  TOOLING = "TOOLING",
  GAME_SERVER = "GAME_SERVER",
  GATEWAY = "GATEWAY",
  AI_AGENT = "AI_AGENT",
}

export interface IAuthToken {
  token: string;
  expiresAt: number;
}

export interface IAuthTokenWithType extends IAuthToken {
  tokenType: AUTH_TOKEN_TYPES;
}

export const getTokens = (baseURL: string, accessToken: string) => {
  return fetchWithErrorHandling(endpoint(baseURL, "list"), buildGetOptions(accessToken));
};

export const extendToken = (
  baseURL: string,
  accessToken: string,
  tokenType: AUTH_TOKEN_TYPES,
  token: string,
  extension: number,
) => {
  return fetchWithErrorHandling(
    endpoint(baseURL, "extend"),
    buildPostOptions(accessToken, {
      tokenType,
      token,
      extension,
    }),
  );
};

export const createToken = (baseURL: string, accessToken: string, tokenType: string, token: string, expiresAt: number) => {
  return fetchWithErrorHandling(
    endpoint(baseURL, "create"),
    buildPostOptions(accessToken, {
      tokenType,
      token,
      expiresAt,
    }),
  );
};

export const revokeToken = (baseURL: string, accessToken: string, tokenType: AUTH_TOKEN_TYPES, token: string) => {
  return fetchWithErrorHandling(
    endpoint(baseURL, "revoke"),
    buildPostOptions(accessToken, {
      tokenType,
      token,
    }),
  );
};

export const removeToken = (baseURL: string, accessToken: string, tokenType: AUTH_TOKEN_TYPES, token: string) => {
  return fetchWithErrorHandling(
    endpoint(baseURL, "remove"),
    buildPostOptions(accessToken, {
      tokenType,
      token,
    }),
  );
};

export const useTokenManagementService = () => {
  const appContext = useContext(AppContext);
  const authUser = useAuthUser();
  const accessToken = authUser()?.access_token;

  return {
    getTokens: () => getTokens(getBaseURL(appContext), accessToken),
    extendToken: (tokenType: AUTH_TOKEN_TYPES, token: string, extension: number) =>
      extendToken(getBaseURL(appContext), accessToken, tokenType, token, extension),
    createToken: (tokenType: string, token: string, expiresAt: number) =>
      createToken(getBaseURL(appContext), accessToken, tokenType, token, expiresAt),
    revokeToken: (tokenType: AUTH_TOKEN_TYPES, token: string) =>
      revokeToken(getBaseURL(appContext), accessToken, tokenType, token),
    removeToken: (tokenType: AUTH_TOKEN_TYPES, token: string) =>
      removeToken(getBaseURL(appContext), accessToken, tokenType, token),
  };
};
